<template>
    <Tabs v-model:value="currentTabIndex">
        <TabList>
            <Tab value="0" as="div" class="flex items-center gap-2">
                <i class="pi pi-home" />
                <span class="font-bold">Detalhes</span>
            </Tab>
            <Tab value="1" as="div" class="flex items-center gap-2">
                <i class="pi pi-wallet" />
                <span class="font-bold">Pagamentos</span>
            </Tab>
        </TabList>

        <TabPanels>
            <TabPanel value="0">
                <section class="flex flex-col gap-2 laptop:mx-5">
                    <div v-if="isStaff" class="flex flex-col gap-2">
                        <label for="user">Profissional</label>
                        <Select v-model="selectedUser" :options="selectUserOptions" optionLabel="name"
                            optionValue="value" class="w-full" filter disabled />
                    </div>

                    <div class="flex flex-col gap-2">
                        <label for="user">Sede</label>
                        <Select v-model="selectedHq" :options="headquartersOptions" optionLabel="name" optionValue="id"
                            class="w-full" disabled @update:modelValue="fieldsChanged()" />
                    </div>

                    <div class="flex flex-col gap-2">
                        <label for="user">Sala</label>
                        <Select v-model="selectedRoom" :options="roomOptions" optionLabel="name" optionValue="id"
                            class="w-full" :disabled="isStaff == false"
                            @update:modelValue="fieldsChanged()" />
                    </div>

                    <div class="flex flex-col gap-2">
                        <label for="user">{{ selectedLocationType == 1 ? 'Data' : 'Próxima data' }}</label>
                        <DatePicker v-model="selectedDate" dateFormat="dd/mm/yy" showIcon fluid :showOnFocus="false"
                            :disabledDays="[0]" :disabledDates="disabledDates" :minDate="minSelectableDate"
                            :maxDate="maxSelectableDate" :disabled="isStaff == false"
                            @update:modelValue="fieldsChanged()" />
                        <small v-if="selectedLocationType == 2" class="mx-auto"><b>Reserva fixa:</b>
                            {{ this.getWeekDayName() }}
                        </small>
                    </div>

                    <div class="flex flex-col gap-2">
                        <label for="user">Horário</label>
                        <Select v-model="selectedHour" :options="selectHoursOptions" optionLabel="name"
                            optionValue="value" optionDisabled="disabled" class="w-full" :disabled="isStaff == false"
                            @update:modelValue="fieldsChanged()" />
                    </div>

                    <div class="flex flex-col gap-2">
                        <label for="user">Tipo de reserva</label>
                        <Select v-model="selectedLocationType" :options="selectLocationTypeOptions" optionLabel="name"
                            optionValue="value" class="w-full" disabled />
                    </div>

                    <div class="flex flex-col gap-3 my-4">
                        <Button class="w-full" label="Cancelar" severity="warn"
                            :disabled="currentStatus.toLowerCase() == 'cancelled'"
                            @click="dialogRevokeIsVisible = true" />
                        <Button v-if="isStaff" class="w-full" label="Atualizar" severity="primary"
                            @click="sendUpdateReservationRequest()" />
                    </div>
                </section>
            </TabPanel>

            <TabPanel value="1">
                <section class="my-2">
                    <DataTable :value="locationPayments" tableStyle="min-width: 50rem">
                        <Column field="id" header="Id" />
                        <Column field="periodName" header="Período" />
                        <Column field="amount" header="Valor">
                            <template #body="slotProps">
                                <span>
                                    {{
                                        parseFloat(slotProps.data.amount).toLocaleString('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                        })
                                    }}
                                </span>
                            </template>
                        </Column>
                        <Column field="status" header="Status">
                            <template #body="slotProps">
                                {{ getPaymentStatus(slotProps.data.status) }}
                            </template>
                        </Column>
                        <Column field="action" header="Ação">
                            <template #body="slotProps">
                                <Button label="Pagar" severity="warn"
                                    @click="sendPaymentRequest(slotProps.data.id, slotProps.data.amount)"
                                    :disabled="slotProps.data.status.toLowerCase() != 'pending' || isStaff" />
                            </template>
                        </Column>

                        <template #empty>
                            <div style="text-align: center; padding: 0.2rem;">
                                Nenhum pagamento encontrado
                            </div>
                        </template>
                    </DataTable>
                </section>
            </TabPanel>
        </TabPanels>
    </Tabs>

    <DialogAgendaPayment v-if="isStaff == false" :dialogIsVisible="dialogPaymentIsVisible" :paymentPixValue="paymentPixValue"
        :paymentPixImageBase64="paymentPixImageBase64" :paymentPixCopyPaste="paymentPixCopyPaste"
        :paymentPixExpirationAt="paymentPixExpirationAt" :paymentPaymentId="paymentPaymentId"
        :paymentAlreadyPaid="paymentAlreadyPaid" :lotePaymentEnabled="false"
        @update:dialogIsVisible="dialogPaymentIsVisible = $event" />

    <DialogAgendaRevoke :dialogIsVisible="dialogRevokeIsVisible" :reservationId="reservationId"
        @update:dialogIsVisible="dialogRevokeIsVisible = $event; $emit('update:dialogIsVisible', $event)" />
</template>

<script>
import { Button, Checkbox, Column, DataTable, DatePicker, Select, Tab, TabList, TabPanel, TabPanels, Tabs } from 'primevue';
import DialogAgendaPayment from './DialogAgendaPayment.vue';
import DialogAgendaRevoke from './DialogAgendaRevoke.vue';
import { isValid, parseISO, format, set } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import reservationService from '@/services/reservationService.js';

import usersService from '@/services/usersService.js';
import headquartersService from '@/services/headquartersService.js';
import paymentService from '@/services/paymentService';
import dateService from '@/services/dateService';

export default {
    name: 'EditLocation',
    components: {
        Select,
        DatePicker,
        Button,
        Checkbox,
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        DataTable,
        Column,
        DialogAgendaPayment,
        DialogAgendaRevoke
    },
    props: {
        reservationId: {
            type: Number,
            required: true
        },
        initialTab: {
            type: String,
            default: 'details'
        }
    },
    emits: ['update:dialogIsVisible'],
    data() {
        return {
            isStaff: this.$store.state.accessLevel == 'admin' || this.$store.state.accessLevel == 'secretary',
            currentTabIndex: "0",

            currentStatus: "",

            // Static fields, can't be changed
            selectedUser: null,
            selectUserOptions: [
                // Example only
                // { name: 'Maria Oliveira', value: 8 },
            ],

            selectedHq: null,
            headquartersOptions: [
                // Example only
                // { name: 'Atlântica - Copacabana', id: 1 }
            ],

            selectedLocationType: null,
            selectLocationTypeOptions: [
                { name: 'Avulsa', value: 1 },
                { name: 'Fixa', value: 2 },
            ],

            // Dynamic fields
            selectedRoom: 1,
            roomOptions: [
                // Example only
                // {
                //     id: 1,
                //     name: 'Sala 01',
                //     color: ''
                // },
            ],

            selectedDate: null,
            selectedDateWeekDay: null,
            minSelectableDate: new Date(),
            maxSelectableDate: new Date(new Date().setDate(new Date().getDate() + 90)),
            disabledDates: [],

            selectedHour: null,
            selectHoursOptions: [],

            // Payment
            locationPayments: [
                // Example only
                // { id: 1, periodName: 'Janeiro/2025', amount: 100.00, status: 'PENDING_PAYMENT', value: "100.00" },
            ],

            dialogRevokeIsVisible: false,

            // Payment
            paymentPixValue: null,
            paymentPixImageBase64: null,
            paymentPixCopyPaste: null,
            paymentPixExpirationAt: null,
            paymentPaymentId: null,
            paymentAlreadyPaid: null,
            dialogPaymentIsVisible: false,
        }
    },
    methods: {
        // Generic methods
        getPaymentStatus(status) {
            const normalized = status.toLowerCase();

            if (normalized === 'paid') return 'Pago';
            if (normalized === 'pending') return 'Pendente';
            if (normalized === 'failed') return 'Falhou';
            if (normalized === 'expired') return 'Expirado';
            return 'Indefinido';
        },
        setCurrentTabFromInitial(tabName) {
            this.currentTabIndex = String(tabName).toLowerCase() === 'payment' ? '1' : '0';
        },
        getWeekDayName() {
            const weekDays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
            return weekDays[this.selectedDateWeekDay];
        },
        formatPeriod(month, year) {
            const date = set(new Date(), { year, month: month - 1, date: 1 });

            return format(date, "MMMM 'de' yyyy", { locale: ptBR });
        },
        getNormalizedSelectedDate() {
            if (this.selectedDate instanceof Date) {
                return isValid(this.selectedDate) ? this.selectedDate : null;
            }

            if (typeof this.selectedDate !== 'string') {
                return null;
            }

            const normalizedDate = this.selectedDate.replaceAll('/', '-');
            const parsedDate = parseISO(normalizedDate);

            return isValid(parsedDate) ? parsedDate : null;
        },
        setCalendarTargetInStore() {
            // Save target date and HQ in store so calendar will focus after redirect
            try {
                const normalizedSelectedDate = this.getNormalizedSelectedDate();
                const dateOnly = normalizedSelectedDate
                    ? format(normalizedSelectedDate, 'yyyy-MM-dd')
                    : typeof (this.selectedDate) === 'string'
                        ? this.selectedDate.replaceAll('/', '-')
                        : null;

                if (!dateOnly) {
                    return;
                }

                this.$store.dispatch('setCalendarTargetDate', { date: dateOnly });
                this.$store.dispatch('setCalendarTargetHq', { hqId: this.selectedHq });
            } catch (e) {
                console.warn('(setCalendarTargetInStore) failed to set calendar target in store', e);
            }
        },
        async openPaymentDialog(pixValue, pixImageBase64, pixCopyPaste, pixPaymentId, pixExpirationAt, alreadyPaid = false) {
            // Save target date and HQ in store so calendar will focus after redirect
            this.setCalendarTargetInStore();

            this.paymentPixValue = pixValue;
            this.paymentPixImageBase64 = pixImageBase64;
            this.paymentPixCopyPaste = pixCopyPaste;
            this.paymentPixExpirationAt = pixExpirationAt
            this.paymentPaymentId = pixPaymentId;
            this.paymentAlreadyPaid = alreadyPaid;
            this.dialogPaymentIsVisible = true;
        },
        // Requests
        async sendPaymentRequest(paymentId, paymentValue) {
            try {
                const paymentRequest = await paymentService.createPayment(paymentId);
                if (paymentRequest.status === 200) {
                    const { invoiceId, paymentStatus, codePixBase64, codePixCopiaCola, paymentDeadline } = paymentRequest.data;

                    this.openPaymentDialog(
                        paymentValue,
                        codePixBase64,
                        codePixCopiaCola,
                        paymentId,
                        paymentDeadline,
                        false
                    );
                } else {
                    throw new Error(`Failed to get payment link, status code is ${paymentRequest.status}`);
                }
            } catch (error) {
                console.error('(sendPaymentRequest) Failed to get payment link', error);
                this.sendToast("Falha de processamento", "Ocorreu uma falha de processamento interna. Tente atualizar a página ou contate o suporte.", "error");
            }
        },
        async sendUpdateReservationRequest() {
            try {
                // Parse data (needed because user change date in <DatePicker> and change date format)
                const normalizedSelectedDate = this.getNormalizedSelectedDate();
                const newDate = normalizedSelectedDate
                    ? format(normalizedSelectedDate, 'yyyy-MM-dd')
                    : typeof (this.selectedDate) === 'string'
                        ? this.selectedDate.replaceAll('/', '-')
                        : null;

                if (!newDate) {
                    throw new Error('Invalid selectedDate value');
                }

                // Send request
                const payload = {
                    "date": newDate,
                    "hour": this.selectedHour,
                    "roomId": this.selectedRoom
                };

                const reservationRequest = await reservationService.updateReservation(this.reservationId, payload);
                if (reservationRequest.status === 200) {
                    this.sendToast("Reserva atualizada", `A reserva foi atualizada com sucesso.`, "success");
                    // Redirect to user calendar
                    this.setCalendarTargetInStore();
                    this.$emit('update:dialogIsVisible', false);


                } else {
                    throw new Error(`Failed to update reservation, status code is ${reservationRequest.status}`);
                }
            } catch (error) {
                if (error.response && error.response.status === 409 && error.response.data.message === "O novo horário e/ou sala desejado já está indisponível.") {
                    return this.sendToast("Horário indisponível", "A combinação de data, hora e sala desejada não está disponível.", "error");
                }

                console.error('(sendUpdateReservationRequest) Failed to update reservation', error);
                this.sendToast("Falha de processamento", "Ocorreu uma falha de processamento interna. Tente atualizar a página ou contate o suporte.", "error");
            }
        },
        fetchReservationDetails(reservationId) {
            reservationService.getReservation(reservationId)
                .then(response => {
                    const reservationData = response.data.booking;

                    this.selectedUser = reservationData.clientId;
                    this.selectUserOptions = [
                        { name: 'Undefined', value: reservationData.clientId }
                    ];

                    this.selectedHq = reservationData.headquarters.id;
                    this.headquartersOptions = [
                        { name: reservationData.headquarters.name, id: reservationData.headquarters.id }
                    ];

                    this.selectedLocationType = reservationData.locationType == 'FIXED' ? 2 : 1;

                    this.selectedRoom = reservationData.room.id;
                    this.roomOptions = [
                        { name: reservationData.room.name, id: reservationData.room.id }
                    ];

                    const isoDate = parseISO(reservationData.dateTime);
                    this.selectedDate = isoDate;
                    this.selectedDateWeekDay = reservationData.weekday ? reservationData.weekday : isoDate.getDay();
                    this.selectedHour = reservationData.hour;

                    this.currentStatus = reservationData.status;

                    if (this.isStaff) {
                        this.selectedUser = reservationData.clientId;
                        this.selectUserOptions = [
                            { name: reservationData.clientName, value: reservationData.clientId },
                        ];

                        this.fetchRoomFromHq();
                        this.tryToLoadRoomAvailabilities();
                    }

                    this.fetchReservationPayments(this.selectedRoom, this.selectedDateWeekDay, this.selectedHour);
                })
                .catch(error => {
                    console.error('(fetchReservationDetails)', error);
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        fetchReservationPayments(roomId, dayOfWeekNumber, hour) {
            let filters;
            if (this.selectedLocationType == 2) {
                filters = { scheduleIdentifier: `room-${roomId}-day-${dayOfWeekNumber}-hour-${hour}` };
            } else {
                filters = { bookingId: this.reservationId };
            }

            usersService.getPayments(filters)
                .then(response => {
                    this.locationPayments = response.data;
                })
                .catch(error => {
                    console.error('(fetchReservationPayments)', error);
                    this.sendToast("Erro ao carregar pagamentos", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        fetchRoomFromHq() {
            headquartersService.getHeadquarters()
                .then(response => {
                    const hqData = response.data.data;

                    const currentHq = hqData.find(hq => hq.id === this.selectedHq);
                    this.roomOptions = currentHq.roomsDetailed.map(room => {
                        return {
                            id: room.id,
                            name: room.name
                        }
                    });
                })
                .catch(error => {
                    console.error('(fetchRoomFromHq)', error);
                    this.sendToast("Erro interno", "Algo deu errado. Tente novamente ou contate o suporte.", "error");
                    throw error;
                });
        },
        fetchBlockedDates() {
            const params = {
                startDate: format(this.minSelectableDate, 'yyyy-MM-dd'),
                endDate: format(this.maxSelectableDate, 'yyyy-MM-dd'),
                isActive: true,
            };
            if (this.selectedHq) {
                params.headquartersId = this.selectedHq;
            }

            dateService.getBlockedDate(params)
                .then(response => {
                    const blockedDatesData = response.data.data;
                    if (!Array.isArray(blockedDatesData)) {
                        console.warn('fetchBlockedDates: response.data.data is not an array', blockedDatesData);
                        return;
                    }

                    this.disabledDates = blockedDatesData.map(blockedDate => {
                        return parseISO(blockedDate.date);
                    });
                })
                .catch(error => {
                    console.error('(fetchBlockedDates)', error);
                });
        },
        fieldsChanged() {
            this.tryToLoadRoomAvailabilities();
        },
        async fetchRoomAvailability(roomId, date) {
            try {
                const response = await reservationService.checkRoomAvailability({ roomId, date });
                return response.data ? response.data : [];
            } catch (error) {
                console.error('(fetchRoomAvailability)', error);
                this.sendToast(
                    "Erro interno",
                    "Algo deu errado. Tente novamente ou contate o suporte.",
                    "error"
                );
                return [];
            }
        },
        async tryToLoadRoomAvailabilities() {
            if (this.selectedDate == null || this.selectedRoom == null) return;

            const normalizedSelectedDate = this.getNormalizedSelectedDate();
            if (normalizedSelectedDate == null) return;

            const formattedDate = format(normalizedSelectedDate, "dd/MM/yyyy");
            const roomAvailability = await this.fetchRoomAvailability(this.selectedRoom, formattedDate);

            this.selectHoursOptions = roomAvailability.map(hourData => {
                let hourIsAvailable;
                if (this.selectedLocationType == 1) {
                    hourIsAvailable = hourData.isAvailableForSingle;
                } else {
                    hourIsAvailable = hourData.isAvailableForFixed;
                }

                return {
                    name: `${hourData.hour}h ${hourIsAvailable ? '' : '(indisponível)'}`,
                    value: hourData.hour,
                    disabled: !hourIsAvailable
                }
            });
        }
    },
    created() {
        this.setCurrentTabFromInitial(this.initialTab);
        this.fetchReservationDetails(this.reservationId);
        this.fetchBlockedDates();
    },
    watch: {
        initialTab(newVal) {
            this.setCurrentTabFromInitial(newVal);
        }
    }
}
</script>

<style></style>